import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

// 951 Bitcoin blog author pubkey
const BLOG_AUTHOR_PUBKEY = '2d3fa5e1397c5a74eec44ca23eebf38660be95aa93437c1aa4da213a16ce4937'; // npub195l6t...

export interface BlogPost {
  id: string;
  pubkey: string;
  createdAt: number;
  title: string;
  summary: string;
  image: string;
  publishedAt: number;
  slug: string;
  hashtags: string[];
  raw: NostrEvent;
}

function getTag(event: NostrEvent, name: string): string {
  return event.tags.find(([t]) => t === name)?.[1] ?? '';
}

function getAllTags(event: NostrEvent, name: string): string[] {
  return event.tags.filter(([t]) => t === name).map(([, v]) => v ?? '');
}

function parseBlogPost(event: NostrEvent): BlogPost | null {
  if (event.kind !== 30023) return null;

  const title = getTag(event, 'title');
  const slug = getTag(event, 'd');

  if (!title || !slug) return null;

  const publishedAtStr = getTag(event, 'published_at');
  const publishedAt = publishedAtStr ? parseInt(publishedAtStr, 10) : event.created_at;

  return {
    id: event.id,
    pubkey: event.pubkey,
    createdAt: event.created_at,
    title,
    summary: getTag(event, 'summary'),
    image: getTag(event, 'image'),
    publishedAt,
    slug,
    hashtags: getAllTags(event, 't'),
    raw: event,
  };
}

export function useBlogPosts() {
  const { nostr } = useNostr();

  return useQuery({
    queryKey: ['blog-posts', BLOG_AUTHOR_PUBKEY],
    queryFn: async () => {
      const filter = [
        {
          kinds: [30023],
          authors: [BLOG_AUTHOR_PUBKEY],
          limit: 20,
        },
      ];

      // Query default relays and Satlantis relay in parallel
      const [defaultEvents, satlantisEvents] = await Promise.allSettled([
        nostr.query(filter),
        nostr.relay('wss://relay.satlantis.io').query(filter),
      ]);

      const allRaw: NostrEvent[] = [
        ...(defaultEvents.status === 'fulfilled' ? defaultEvents.value : []),
        ...(satlantisEvents.status === 'fulfilled' ? satlantisEvents.value : []),
      ];

      // Deduplicate by event ID
      const seen = new Set<string>();
      const deduped = allRaw.filter((e) => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });

      const posts = deduped
        .map(parseBlogPost)
        .filter((p): p is BlogPost => p !== null);

      // Sort newest first
      posts.sort((a, b) => b.publishedAt - a.publishedAt);

      return posts;
    },
    staleTime: 60000,
  });
}

export const BLOG_AUTHOR_NPUB = 'npub195l6tcfe03d8fmkyfj3ra6lnsesta9d2jdphcx4ymgsn59kwfyms6nvvre';
